import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { useNavigate } from 'react-router';
import { styled } from '@mui/material';
import { Box, Container, Grid, Typography } from '@material-ui/core';
import Navbar from 'components/Navbar';
import Footer from 'components/Footer/Index';
import { IJob } from 'core/interface/job';
import NoData from 'components/shared/NoData/Index';
import useLocales from 'hooks/useLocales';
import Pagination from '@material-ui/core/Pagination';

import { JobService } from 'services/jobs/job';
import Image from 'components/image/Image';
import { degreeTypes } from 'core/constants/aboutMePopUpConstants';
import { contractTypes } from 'core/constants/workType';

import PublicJobFilters from './publicJobFilters';

import './index.scss';
import { getSalaryByValue } from 'constants/constants';
import colors from 'assets/variables/colors';

const RootStyle = styled('div')(() => ({
  position: 'relative',
  marginTop: '0',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh'
}));

const WrapperContent = styled('div')(() => ({
  position: 'relative',
  flex: 1
}));

const GridStyle = styled(Grid)(() => ({
  '@media (max-width: 959px)': {
    flexWrap: 'wrap',
    '.MuiAutocomplete-fullWidth': {
      width: '100%'
    },
    '.inputs-group': {
      maxWidth: '100%'
    },
    '.MuiAutocomplete-hasPopupIcon': {
      maxWidth: '100%'
    },
    div: {
      maxWidth: '100%'
    }
  }
}));

export const JobCard = styled(Box)(() => ({
  padding: '35px 50px',
  width: '100%',
  background: '#FFFFFF',
  boxShadow: '0px 0px 12px rgba(0, 0, 0, 0.08)',
  borderRadius: '25px',
  marginBottom: '30px',
  cursor: 'pointer'
}));

export const CompetencyTag = styled('div')(
  ({
    tenantColor,
    lighterPurple
  }: {
    tenantColor: string;
    lighterPurple: string;
  }) => ({
    display: 'inline-block',
    padding: '10px 15px',
    margin: '5px',
    backgroundColor: lighterPurple,
    borderRadius: '15px',
    fontSize: '12px',
    fontWeight: 500,
    color: tenantColor
  })
);

export const CompetencyContainer = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  marginTop: '15px',
  marginBottom: '20px'
}));

const JobTitle = styled(Typography)(() => ({
  fontFamily: 'Rubik',
  fontWeight: 600,
  fontSize: '24px',
  color: '#131417',
  marginBottom: '10px'
}));

export const JobDescription = styled(Typography)(() => ({
  fontSize: '14px',
  color: '#666',
  marginBottom: '15px'
}));

export const JobDetailWrapper = styled(Typography)(() => ({
  fontSize: '14px',
  color: '#333',
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: '10px',
  '& svg': {
    marginRight: '8px',
    fontSize: '18px'
  },
  '& strong': {
    marginRight: '5px',
    minWidth: '80px',
    display: 'inline-block'
  }
}));

export const CompetencyTitle = styled(Typography)(() => ({
  fontSize: '16px',
  fontWeight: 600,
  color: '#666',
  marginTop: '20px',
  marginBottom: '20px'
}));

// Add this type for location cache
interface LocationCache {
  [key: string]: string; // locationId -> locationName
}

const PublicJobs: FC = (): JSX.Element => {
  const { t } = useLocales();
  const navigate = useNavigate();
  const { jobs } = useSelector((state: RootState) => state.jobs);
  const { cities } = useSelector((state: RootState) => state.locations);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  // Add state to cache location names
  const [locationCache, setLocationCache] = useState<LocationCache>({});

  useEffect(() => {
    setOffset((page - 1) * limit);
  }, [page, limit]);

  useEffect(() => {
    // Fetch cities data for location mapping
    getCities();

    // Fetch jobs data
    getJobs(limit, offset);
  }, [limit, offset]);

  // Add effect to fetch location data for jobs when jobs change
  useEffect(() => {
    if (jobs?.results?.length) {
      fetchMissingLocationData(jobs.results);
    }
  }, [jobs?.results]);

  const getCities = (limit: number = 20919, q?: string) => {
    JobService.getLocations({
      limit,
      q
    });
  };

  const getJobs = async (
    limit?: number,
    offset?: number,
    filters?: any
  ): Promise<void> => {
    try {
      const params = {
        limit,
        offset,
        public: true,
        ...(filters || {})
      };

      JobService.getJobs(params);
    } catch (error) {
      console.log(error);
    }
  };

  // New function to fetch missing location data
  const fetchMissingLocationData = async (jobsList: IJob[]) => {
    // Collect all location IDs from jobs
    const allLocationIds = new Set<string | number>();
    jobsList.forEach((job) => {
      if (job.locations && job.locations.length) {
        job.locations.forEach((location) => {
          const locationId =
            typeof location === 'object' ? location.id : location;
          if (locationId && !locationCache[locationId]) {
            allLocationIds.add(locationId);
          }
        });
      }
    });

    // If we have missing locations, fetch them
    if (allLocationIds.size > 0) {
      const locationIdsArray = Array.from(allLocationIds);

      // Fetch each location individually
      const fetchPromises = locationIdsArray.map(async (locationId) => {
        try {
          const locationData = await JobService.getLocationById(locationId);
          if (locationData && locationData.display_name) {
            return { id: locationId, name: locationData.display_name };
          }
        } catch (error) {
          console.error(`Error fetching location ${locationId}:`, error);
        }
        return null;
      });

      // Wait for all fetches to complete
      const results = await Promise.all(fetchPromises);

      // Update cache with results
      const newCache = { ...locationCache };
      results.forEach((result) => {
        if (result) {
          newCache[result.id] = result.name;
        }
      });

      // Update cache
      setLocationCache(newCache);
    }
  };

  // Helper function to get location names from location IDs
  const getLocationNames = (locationIds: any[]) => {
    if (!locationIds || !locationIds.length) {
      return [];
    }

    const locationNames: string[] = [];

    locationIds.forEach((location) => {
      // If location already has display_name, use it directly
      if (location.display_name) {
        locationNames.push(location.display_name);
        return;
      }

      // Otherwise, find the location in cities by ID
      const locationId = typeof location === 'object' ? location.id : location;

      // First check our cache
      if (locationCache[locationId]) {
        locationNames.push(locationCache[locationId]);
        return;
      }

      // Then check the cities in Redux store
      if (cities && cities.length > 0) {
        const city = cities.find(
          (city: any) => String(city.id) === String(locationId)
        );

        if (city) {
          locationNames.push(city.display_name);

          // Also add to our cache for future use
          setLocationCache((prev) => ({
            ...prev,
            [locationId]: city.display_name
          }));
          return;
        }
      }
    });

    return locationNames;
  };

  const renderJobCard = (job: IJob) => {
    // Get location names for this job
    const locationNames = getLocationNames(job.locations);

    return (
      <JobCard
        key={job.id}
        onClick={() => navigate(`/jobs/${job.slug || job.id}`)}
      >
        <Grid
          item
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start'
          }}
        >
          <Grid display="flex" alignItems="start" width="100%">
            <Grid item xs={7} sm={8}>
              <JobTitle variant="h5">{job.title}</JobTitle>
              <JobDescription variant="body2">{job.description}</JobDescription>
            </Grid>
            <Grid
              item
              xs={5}
              sm={4}
              style={{ textAlign: 'right', height: '80px' }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  height: '100%'
                }}
              >
                <Image
                  name={
                    job.company.logo || '/static/images/recrout-logo-letter.jpg'
                  }
                  url={
                    job.company.logo || '/static/images/recrout-logo-letter.jpg'
                  }
                  style={{
                    width: 'auto',
                    maxWidth: '100%',
                    height: 'auto',
                    maxHeight: '80px',
                    objectFit: 'contain'
                  }}
                />
              </Box>
            </Grid>
          </Grid>
          <Grid container spacing={2} mt="20px">
            <Grid item xs={12} sm={6}>
              <JobDetailWrapper>
                <strong>{t('Company')}: </strong> {job.company.name}
              </JobDetailWrapper>
              {locationNames.length > 0 && (
                <JobDetailWrapper>
                  <strong>{t('Location')}: </strong>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      wordBreak: 'break-word'
                    }}
                  >
                    {locationNames.join(', ')}
                  </div>
                </JobDetailWrapper>
              )}
              {job.contract && (
                <JobDetailWrapper>
                  <strong>{t('Contract')}: </strong>
                  {Array.isArray(job.contract)
                    ? job.contract
                        .map(
                          (c) =>
                            contractTypes.find((type) => type.value === c)
                              ?.to_show || ''
                        )
                        .filter(Boolean)
                        .join(', ')
                    : contractTypes.find((c) => c.value === job.contract)
                        ?.to_show || ''}
                </JobDetailWrapper>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {job.compensation && (
                <JobDetailWrapper>
                  <strong>{t('Compensations (EUR)')}: </strong>{' '}
                  {Array.isArray(job.compensation)
                    ? job.compensation
                        .map((comp) => getSalaryByValue(comp))
                        .filter(Boolean)
                        .join(', ')
                    : getSalaryByValue(job.compensation)}
                </JobDetailWrapper>
              )}
              {job.degree && (
                <JobDetailWrapper>
                  <strong>{t('Education')}: </strong>
                  {degreeTypes.find((d) => d.value === job.degree)?.to_show ||
                    ''}
                </JobDetailWrapper>
              )}
              {job.work_exp && (
                <JobDetailWrapper>
                  <strong>{t('Experience')}: </strong> {job.work_exp}{' '}
                  {t('years')}
                </JobDetailWrapper>
              )}
            </Grid>
          </Grid>
          <CompetencyTitle>
            {t('We are looking for these competencies')}:
          </CompetencyTitle>
          {job.competencies && job.competencies.length > 0 && (
            <CompetencyContainer>
              {job.competencies.map((comp, index) => (
                <CompetencyTag
                  key={index}
                  tenantColor={colors.tenantColor}
                  lighterPurple={colors.lightPurple}
                >
                  {comp.competency.name}
                </CompetencyTag>
              ))}
            </CompetencyContainer>
          )}
        </Grid>
      </JobCard>
    );
  };

  return (
    <RootStyle>
      <Navbar />
      <div
        className="public-companies-banner"
        style={{ backgroundColor: colors.tenantColor }}
      >
        <div className="banner-content">
          <h1>{t('Discover Great career opportunities')}</h1>
          <p>
            {t('Browse through our latest job listings and find your next career opportunity')}
          </p>
        </div>
      </div>
      <WrapperContent>
        <Container
          maxWidth="lg"
          style={{ paddingTop: '50px', paddingBottom: '50px' }}
        >
          <div className="back_buttons" onClick={() => navigate(-1)}>
            <Image name="arrow-left.svg" />
            {t('Back')}
          </div>
          <GridStyle container spacing={3}>
            <Grid item xs={12} lg={3} className="inputs-group">
              <PublicJobFilters
                applyFilter={(filters) => {
                  setPage(1);
                  getJobs(limit, 0, filters);
                }}
              />
            </Grid>
            <Grid item xs={12} lg={9}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
              >
                {!jobs?.results || jobs.results.length === 0 ? (
                  <NoData />
                ) : (
                  <>
                    {jobs.results.map((job: IJob) => renderJobCard(job))}
                    <Box display="flex" justifyContent="center" mt={4}>
                      <Pagination
                        className="pagination"
                        count={Math.ceil((jobs?.count || 0) / limit)}
                        page={page}
                        onChange={(_, value) => setPage(value)}
                        variant="outlined"
                        shape="rounded"
                        showFirstButton
                        showLastButton
                      />
                    </Box>
                  </>
                )}
              </Box>
            </Grid>
          </GridStyle>
        </Container>
      </WrapperContent>
      <Footer />
    </RootStyle>
  );
};

export default PublicJobs;
